import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    // Initialize user from localStorage synchronously to prevent flash
    const savedUser = localStorage.getItem('pop_user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [loading, setLoading] = useState(true);

  const clearAuth = useCallback(() => {
    localStorage.removeItem('pop_token');
    localStorage.removeItem('pop_user');
    localStorage.removeItem('adminToken'); // Also clear legacy admin token
    setUser(null);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('pop_token');
    const savedUser = localStorage.getItem('pop_user');
    
    if (token && savedUser) {
      // Set user immediately from localStorage
      try {
        setUser(JSON.parse(savedUser));
      } catch (e) {
        clearAuth();
      }
      
      // Verify token is still valid in background
      authAPI.me()
        .then((res) => {
          setUser(res.data);
          localStorage.setItem('pop_user', JSON.stringify(res.data));
        })
        .catch((err) => {
          // Only clear auth if it's a 401 error - network errors shouldn't log out
          if (err.response?.status === 401) {
            clearAuth();
          }
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [clearAuth]);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('pop_token', access_token);
    localStorage.setItem('pop_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password) => {
    const response = await authAPI.register({ name, email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('pop_token', access_token);
    localStorage.setItem('pop_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const logout = useCallback(() => {
    clearAuth();
  }, [clearAuth]);

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, isAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
